import React from 'react';
import { Info } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '../../ui/popover';

export const generationFieldHelp = {
  facilityBasis: {
    label: 'Facility basis',
    description: 'The site power requirement recorded in Project Intent. It seeds the working facility-load assumption but does not change when this scenario is edited.',
  },
  peakDryBulb: {
    label: 'Peak dry bulb',
    description: 'The high ambient air temperature from the Site Evidence Document. It identifies the climate basis behind planning derates; vendor correction curves are still required.',
  },
  peakWetBulb: {
    label: 'Peak wet bulb',
    description: 'The Site Evidence Document wet-bulb temperature used to describe the cooling design condition. This planning screen does not run a detailed cooling-tower heat balance.',
  },
  parcel: {
    label: 'Parcel',
    description: 'Mapped site area in acres. Compare it with the planning footprint, allowing for setbacks, access, drainage, construction laydown, and other non-generation uses.',
  },
  normalNet: {
    label: 'Normal net',
    description: 'Total site-rated output from all blocks after ambient and degradation factors, auxiliary demand, and any enabled carbon-capture load.',
  },
  largestGroupOut: {
    label: 'Contingency loss',
    description: 'Net output removed by the selected resilience criterion. Groups are ranked by net MW and the largest one, two, or three are removed for N-1, N-2, or N-3.',
  },
  nMinusOneLoadMargin: {
    label: 'Contingency load margin',
    description: 'Output remaining under the selected resilience criterion minus critical load. A non-negative value passes the load-only criterion; a negative value is the shortfall.',
  },
  nMinusOneReserveMargin: {
    label: 'Contingency + reserve margin',
    description: 'Output remaining under the selected resilience criterion minus critical load plus the user-entered reserve requirement. A negative value is the capacity shortfall.',
  },
  scenarioName: {
    label: 'Scenario name',
    description: 'A clear identifier for this combination of load, technology, capacity, and impact assumptions. It is used in saved scenarios and exported layout filenames.',
  },
  loadBasis: {
    label: 'Facility load source',
    description: 'Choose the recorded project requirement or cumulative phase, or use a manual scenario. IT MW converts to facility MW once using a valid declared or explicitly adopted PUE; facility MW already includes overhead. Unknown basis or IT-conversion PUE stays unresolved. Selecting a resolved source sets facility and critical load; both remain editable.',
  },
  technology: {
    label: 'Technology',
    description: 'Initially matches the selected Project Intent phase when possible. Users can override it for a scenario; changing technology reloads its sourced planning defaults and does not edit Project Intent until promotion.',
  },
  facilityLoad: {
    label: 'Facility load',
    description: 'Expected maximum electrical demand of the complete facility, in MW. It contributes to resource calculations and is the fallback critical load when no separate value is entered.',
  },
  criticalLoad: {
    label: 'Critical load',
    description: 'Demand that must remain served after the largest contingency. Leave blank to use facility load; enter a lower value only when a documented load-shedding basis exists.',
  },
  reserveMargin: {
    label: 'Reserve margin',
    description: 'Additional capacity required above critical load for the N-1 plus reserve test. The scenario starts at 15%, but this user-entered percentage directly controls the reserve requirement.',
  },
  resilienceCriterion: {
    label: 'Resilience criterion',
    description: 'Select how many largest independent contingency groups the configured fleet must survive. N tests normal operation; N+1 / N-1 removes one group; N+2 / N-2 removes two.',
  },
  contingencyBlocks: {
    label: 'Contingency blocks',
    description: 'Number of independently modelled generation blocks. Each block is treated as its own contingency group, so one complete block is removed for the N-1 test.',
  },
  isoGrossPerBlock: {
    label: 'ISO gross per block',
    description: 'Nameplate gross output of each block at ISO reference conditions, in MW. Site ambient and degradation factors are applied before auxiliary and capture loads are subtracted.',
  },
  ambientFactor: {
    label: 'Site / firm output factor',
    description: 'User-entered percentage of nameplate counted as firm site output. For gas this is the ambient correction; for grid, storage, renewables, and nuclear it starts from the project capacity-credit default.',
  },
  degradationFactor: {
    label: 'Degradation factor',
    description: 'User-entered percentage retained after equipment degradation. It multiplies ISO gross output after the ambient factor and directly changes normal and N-1 capacity.',
  },
  auxiliaryLoad: {
    label: 'Auxiliary load per block',
    description: 'User-entered station-service demand for one block, in MW. It is subtracted from site-rated gross output before normal and N-1 net capacity are calculated.',
  },
  ccsEnabled: {
    label: 'Carbon capture load',
    description: 'Includes the planning load, water use, and footprint for CO₂ capture, conditioning, and compression up to the pipeline export boundary. Transport, injection, and storage remain external.',
  },
  firmGasCapacity: {
    label: 'Firm gas capacity',
    description: 'SUPPLY evidence: the firm natural-gas delivery capacity the utility or pipeline has committed to this site, in dekatherms per day. It cannot be computed — it comes from a commitment letter or contract. The risk screen compares it with the calculated peak daily pipeline nomination (the DEMAND); if the figure is not committed yet, use that computed nomination as the number to request.',
  },
  waterCapacity: {
    label: 'Consumptive-use water capacity',
    description: 'Committed consumptive-use limit in gallons per day, compared with modeled generation, capture and cooling consumption. A municipal service allocation or withdrawal limit measures a different quantity and is shown separately. Adequacy remains unresolved until both the consumption basis and the applicable limit are documented.',
  },
  co2ExportCapacity: {
    label: 'CO₂ export capacity',
    description: 'SUPPLY evidence: the confirmed annual capacity of the downstream CO₂ transport and storage route, in metric tonnes per year. It is compared with the computed captured CO₂ (the DEMAND) when capture is enabled; if the route is not confirmed yet, that computed figure is the number to request from the offtaker.',
  },
  dataCenterWithdrawal: {
    label: 'Data-center withdrawal',
    description: 'Water the data-center cooling system takes in, per MWh of facility load. A demand parameter of the facility, not a site limit; a cooling-type planning default can stand in until an engineered basis replaces it.',
  },
  dataCenterConsumption: {
    label: 'Data-center consumption',
    description: 'Water the data-center cooling system evaporates (does not return), per MWh of facility load. It completes the campus water total; without it the peak total water demand stays indeterminate.',
  },
  coolingType: {
    label: 'Cooling type',
    description: 'Applies a low-confidence planning-default water intensity for the selected data-center cooling family so the campus water total can compute. The default carries its citation as provenance and is replaced by any user- or evidence-supplied basis.',
  },
  sourceSoundPower: {
    label: 'Source sound power',
    description: 'Vendor sound-power level for one generation block, in A-weighted decibels. Sound power is a source property, not the sound-pressure level measured at a receptor.',
  },
  nearestReceptor: {
    label: 'Nearest receptor',
    description: 'Straight-line distance in metres from the generation source to the nearest noise-sensitive receptor. The screen applies geometric spreading only and does not replace an acoustic study.',
  },
  barrierLoss: {
    label: 'Barrier loss',
    description: 'Assumed attenuation in dB from a verified barrier or terrain screen. Use zero unless the source–receptor path and barrier geometry support a defensible insertion loss.',
  },
  fixtureCount: {
    label: 'Fixture count',
    description: 'Total exterior-lighting fixtures in the scenario. Together with watts and lumens per fixture, it calculates installed lighting power and total emitted lumens.',
  },
  wattsPerFixture: {
    label: 'Watts per fixture',
    description: 'Rated electrical input of one exterior fixture. Multiplied by fixture count to estimate installed lighting power; operating hours and energy use are not calculated here.',
  },
  lumensPerFixture: {
    label: 'Lumens per fixture',
    description: 'Rated luminous flux of one exterior fixture. Multiplied by fixture count for an inventory total; it does not predict illuminance or property-line light trespass.',
  },
  fullyShielded: {
    label: 'Fully shielded',
    description: 'Confirm that fixtures emit no light above the horizontal and direct light downward. This records a planning control; verify the selected luminaire photometry.',
  },
  bugRated: {
    label: 'BUG rated',
    description: 'Confirm that Backlight, Uplight, and Glare ratings are available for the selected exterior fixtures. The rating values should be checked against the applicable lighting zone.',
  },
  curfewControls: {
    label: 'Curfew / controls',
    description: 'Confirm that non-safety lighting can be dimmed or switched by curfew, occupancy, or motion controls to reduce night-time impacts.',
  },
  iesFileAvailable: {
    label: 'IES file available',
    description: 'Confirm that manufacturer IES photometric files are available. These files are required for a later site-specific illuminance and light-trespass model.',
  },
  peakGas: {
    label: 'Peak gas',
    description: 'Calculated full-output natural-gas input in MMBtu per hour, using site-rated gross output and the selected technology’s HHV heat-rate archetype.',
  },
  pipelineNomination: {
    label: 'Pipeline nomination',
    description: 'Peak gas input converted to a 24-hour daily nomination in Dth/day. This is a screening demand, not a hydraulic confirmation of pressure and deliverability.',
  },
  residualCo2: {
    label: 'Modeled on-site generation residual CO₂',
    description: 'Annual on-site generation combustion CO₂ remaining after the enabled capture fraction, based on annual operating hours and the natural-gas HHV emissions factor. Purchased-grid electricity and unmodeled standby operation are excluded; zero does not mean zero site emissions.',
  },
  capturedCo2: {
    label: 'Modeled on-site generation captured CO₂',
    description: 'Annual on-site generation CO₂ sent to the pipeline export boundary under the planning capture rate. It excludes purchased-grid electricity, unmodeled standby operation, downstream transport losses and storage performance.',
  },
  waterConsumption: {
    label: 'Modeled component water consumption',
    description: 'Calculated daily equipment/process consumption for generation, enabled carbon capture and data-center cooling where its coefficient is declared. This is a component subtotal, not whole-site water demand. Makeup, initial fill, flushing, leakage, humidification and domestic uses outside the declared coefficients are not calculated; a declared dry-cooling zero retains its owner or source assumptions.',
  },
  planningFootprint: {
    label: 'Planning footprint',
    description: 'Screening land area for generation equipment, cooling, switchyard, and enabled capture/compression facilities. It excludes many site-wide constraints and is not a final layout.',
  },
} as const;

export type GenerationFieldHelpId = keyof typeof generationFieldHelp;

export function GenerationFieldHelp({ field }: { field: GenerationFieldHelpId }): React.JSX.Element {
  const entry = generationFieldHelp[field];

  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={`Explain ${entry.label}`}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-text-muted transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        data-testid={`generation-help-${field}`}
      >
        <Info className="h-2.5 w-2.5" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 border border-border p-3 rounded-lg" data-testid={`generation-help-${field}-content`}>
        <PopoverHeader>
          <PopoverTitle className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">{entry.label}</PopoverTitle>
          <PopoverDescription className="text-xs normal-case leading-relaxed text-text-secondary">{entry.description}</PopoverDescription>
        </PopoverHeader>
      </PopoverContent>
    </Popover>
  );
}

export function GenerationFieldLabel({
  field,
  htmlFor,
  children,
  className = '',
}: {
  field: GenerationFieldHelpId;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <label htmlFor={htmlFor}>{children}</label>
      <GenerationFieldHelp field={field} />
    </span>
  );
}
